import { broadcastRoutes } from '../broadcasts/routes'
import { dashboardRoutes } from '../dashboard/routes'
import { employeeRoutes } from '../employees/routes'
import { shiftRoutes } from '../shifts/routes'
import { siteRoutes } from '../sites/routes'
import { timeOffRoutes } from '../time-off/routes'
import { NavItem, OrgNavContext, OrgRouteName } from './types/navigation.types'

/*
|--------------------------------------------------------------------------
| Organisation header navigation
|--------------------------------------------------------------------------
|
| One link per day-to-day area, each landing on the page people work in.
| The rest of an area is reached from that page rather than from a second
| navigation bar: activity from the calendar's toolbar or the shift list,
| buildings, skills, job titles, job roles and reasons from the tabs of the
| organisation settings.
|
| `activeFor` names the other routes that belong to a link so it stays
| highlighted on them. Detail and create pages are siblings of their list
| in the route tree, not children, so matching the list alone would not
| cover them.
*/
export const orgHeaderNav = [
  {
    kind: 'link',
    id: 'dashboard',
    label: 'Dashboard',
    // Staff have no org screens this release (web-30 Q4, E2E-R4-01)
    visible: (ctx) => ctx.manager,
    to: ({ org }) => dashboardRoutes.dashboard(org),
  },
  {
    kind: 'link',
    id: 'schedule',
    label: 'Schedule',
    // Staff have no org screens this release (web-30 Q4, E2E-R4-01)
    visible: (ctx) => ctx.manager,
    to: ({ org }) => shiftRoutes.calendar(org),
    // The shift wizard highlights New Shift instead (HeaderNavigation)
    activeFor: ['/(app)/[org]/schedule/activity/'],
  },
  {
    kind: 'link',
    id: 'shifts',
    label: 'Shifts',
    // Staff have no org screens this release (web-30 Q4, E2E-R4-01)
    visible: (ctx) => ctx.manager,
    to: ({ org }) => shiftRoutes.list(org),
    activeFor: ['/(app)/[org]/schedule/shifts/[id]'],
  },
  {
    kind: 'link',
    id: 'people',
    label: 'People',
    // Staff have no org screens this release (web-30 Q4, E2E-R4-01)
    visible: (ctx) => ctx.manager,
    to: ({ org }) => employeeRoutes.list(org),
    activeFor: [
      '/(app)/[org]/people/employees/[id]',
      '/(app)/[org]/people/employees.create',
    ],
  },
  {
    kind: 'link',
    id: 'time-off',
    label: 'Time Off',
    to: ({ org }) => timeOffRoutes.list(org),
    // Staff have no org screens this release (E2E-R4-01)
    visible: (ctx) => ctx.manager && ctx.feature('time-off'),
    activeFor: [
      '/(app)/[org]/people/time-off/[id]',
      '/(app)/[org]/people/time-off.create',
    ],
  },
  {
    kind: 'link',
    id: 'broadcasts',
    label: 'Broadcasts',
    // Staff have no org screens this release (web-30 Q4, E2E-R4-01)
    visible: (ctx) => ctx.manager,
    to: ({ org }) => broadcastRoutes.list(org),
    activeFor: [
      '/(app)/[org]/people/broadcasts/[id]',
      '/(app)/[org]/people/broadcasts.create',
    ],
  },
  {
    kind: 'link',
    id: 'settings',
    label: 'Settings',
    to: ({ org }) => siteRoutes.settings(org),
    // Staff can't open settings (web-30 Q4)
    visible: (ctx) => ctx.manager,
    activeFor: [
      '/(app)/[org]/settings/(organizations)',
      '/(app)/[org]/settings/buildings/[id]',
      '/(app)/[org]/settings/buildings.create',
      '/(app)/[org]/settings/skills/[id]',
      '/(app)/[org]/settings/skills.create',
      '/(app)/[org]/settings/shift-reasons/[id]',
      '/(app)/[org]/settings/shift-reasons.create',
      '/(app)/[org]/settings/groups/',
      '/(app)/[org]/settings/groups/[id]',
      '/(app)/[org]/settings/groups.create',
    ],
  },
] as const satisfies readonly NavItem<OrgNavContext, OrgRouteName>[]
